'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Alert from '@components/Widgets/Alert/Alert.react';
import { getLoggedServingsOfRecipe, getParticipantsForSpecificMeal } from '@utils/Meals';
const getParticipantsForSpecificMealFromPlan = getParticipantsForSpecificMeal;
import { calculateParticipantServings, calculateTotalServingsForParticipants } from '@utils/servingsCalculations';
import './EditMealServingsModalPrimary.scss';

export default class LeftoverAllocationsSection extends Component {
    static propTypes = {
        dishes: PropTypes.array.isRequired,
        contents: PropTypes.object.isRequired,
        plan: PropTypes.object,
        mealDishes: PropTypes.array,
        pendingLeftovers: PropTypes.object,
        extraServings: PropTypes.number.isRequired,
        dishServings: PropTypes.array.isRequired,
        isPro: PropTypes.bool.isRequired,
        user: PropTypes.object.isRequired,
        onAddEditLeftovers: PropTypes.func.isRequired,
        onChangeExtraServings: PropTypes.func.isRequired,
    };

    static defaultProps = {
        plan: null,
        mealDishes: null,
        pendingLeftovers: null,
    };

    /**
     * Format participants for display with guest count
     * @private
     */
    formatParticipantsForDisplay = (participants) => {
        const nonGuestParticipants = participants.filter(p => p.uuid !== "");
        const guestCount = participants.length - nonGuestParticipants.length;
        const participantsToDisplay = nonGuestParticipants.map(p => p.name);

        if (guestCount > 0) {
            participantsToDisplay.push(`${guestCount} ${guestCount > 1 ? 'Guests' : 'Guest'}`);
        }

        return participantsToDisplay.join(', ');
    }

    render() {
        const { dishes, contents, plan, mealDishes, pendingLeftovers, extraServings, dishServings, isPro, user, onAddEditLeftovers, onChangeExtraServings } = this.props;

        const isMultiDish = mealDishes && mealDishes.length > 1;

        let unassigned = 0;
        let unassignedBreakdown = null;

        if (isMultiDish) {
            // For multi-dish, calculate per-dish unassigned
            unassignedBreakdown = [];
            dishServings.forEach(({uuid, title, totalServings, totalYield, totalLeftoverServings}, index) => {
                // Find corresponding dish to get original index
                const dish = dishes.find(d => (d.uuid || d.id) === uuid);
                const dishNumber = dish?.main?.originalDishIndex || (index + 1);

                const dishUnassigned = totalYield - totalServings - extraServings;
                unassignedBreakdown.push({
                    dishNumber: dishNumber,
                    dishName: title,
                    servings: dishUnassigned
                });
                unassigned += dishUnassigned;
            });
        } else {
            // For single dish, calculate total
            dishServings.forEach(({totalServings, totalYield, totalLeftoverServings}) => {
                const dishUnassigned = totalYield - totalServings - extraServings;
                unassigned = unassigned + dishUnassigned;
            });
        }

        let leftovers = [];

        // If pending leftovers exist, use them as the complete source of truth
        if (pendingLeftovers && pendingLeftovers.leftovers) {
            leftovers = pendingLeftovers.leftovers.map(leftover => {
                const participants = leftover.participants;

                // Calculate servings - either total or per-dish breakdown
                let totalServings = 0;
                let dishServingsBreakdown = null;

                if (isMultiDish) {
                    // Multi-dish scenario - create breakdown from current dishes
                    // Pending leftovers inherit servings from parent meal's logged_amount
                    dishServingsBreakdown = [];

                    dishes.forEach((dish, index) => {
                        const dishContent = contents[dish.main.recipe_uuid || dish.main.food_uuid];
                        const dishNumber = dish.main?.originalDishIndex || (index + 1);
                        const dishName = dishContent?.title || dishContent?.name || `Dish ${dishNumber}`;

                        // Use utility to calculate servings for all participants on this dish
                        const dishTotalServings = calculateTotalServingsForParticipants(participants, dish.main, dishContent);

                        dishServingsBreakdown.push({
                            dishNumber: dishNumber,
                            dishName: dishName,
                            servings: dishTotalServings
                        });

                        totalServings += dishTotalServings;
                    });
                } else {
                    // Single dish - calculate servings properly for all participants
                    // Use first dish since it's single-dish scenario
                    const dish = dishes[0];
                    const dishContent = contents[dish.main.recipe_uuid || dish.main.food_uuid];
                    totalServings = calculateTotalServingsForParticipants(participants, dish.main, dishContent);
                }

                const participantsDisplay = this.formatParticipantsForDisplay(participants);

                // Handle both consumer (date-based) and PRO (offset-based) data models
                let displayDate;
                if (leftover.leftoverDate && leftover.leftoverDate.format) {
                    // Consumer side: has leftoverDate (moment object)
                    displayDate = leftover.leftoverDate.format('YYYY-MM-DD');
                } else if (leftover.leftoverOffset !== undefined) {
                    // PRO side: has leftoverOffset (integer) - use Day X format
                    displayDate = `Day ${leftover.leftoverOffset + 1}`;
                } else {
                    // Fallback
                    displayDate = 'Unknown';
                }

                return {
                    date: displayDate,
                    mealType: leftover.mealType,
                    participants: participantsDisplay,
                    servings: totalServings,
                    dishServingsBreakdown: dishServingsBreakdown,
                    isMultiDish: isMultiDish,
                    isPending: false // Treat as regular leftovers, not pending
                };
            });
        } else {
            // Fallback to existing leftovers if no pending leftovers
            // IMPORTANT: Aggregate leftovers from ALL dishes, not just the first one
            const allMealLeftovers = [];
            dishes.forEach(dish => {
                if (dish.leftovers && dish.leftovers.length > 0) {
                    allMealLeftovers.push(...dish.leftovers);
                }
            });

            // Group leftovers by date and meal type to display them as single cards
            const leftoversByDateMeal = {};
            allMealLeftovers.forEach(leftover => {
                // For PRO plans, use offset instead of date
                const dateOrOffset = plan ? leftover.offset : leftover.date;
                const key = `${dateOrOffset}_${leftover.meal}`;
                if (!leftoversByDateMeal[key]) {
                    leftoversByDateMeal[key] = [];
                }
                leftoversByDateMeal[key].push(leftover);
            });

            // Now iterate through grouped leftovers to create display cards
            leftovers = Object.entries(leftoversByDateMeal).map(([key, leftoverGroup]) => {
                // All leftovers in a group share the same date, meal, and participants
                const firstLeftover = leftoverGroup[0];
                const participants = plan ? getParticipantsForSpecificMealFromPlan(firstLeftover, user) : getParticipantsForSpecificMeal(firstLeftover, user);
                const participantsDisplay = this.formatParticipantsForDisplay(participants);

                // Calculate servings - either total or per-dish breakdown
                let totalServings = 0;
                let dishServingsBreakdown = null;

                if (isMultiDish && leftoverGroup.length > 1) {
                    // For multi-dish scenarios, create per-dish breakdown
                    dishServingsBreakdown = [];

                    leftoverGroup.forEach((leftover, index) => {
                        const dishContent = contents[leftover.recipe_uuid || leftover.food_uuid];
                        const dishTotalServings = calculateTotalServingsForParticipants(participants, leftover, dishContent);

                        // Find the parent dish from dishes array to get original index
                        const parentDish = dishes.find(d =>
                            (d.main.recipe_uuid || d.main.food_uuid) === (leftover.recipe_uuid || leftover.food_uuid)
                        );
                        const dishNumber = parentDish?.main?.originalDishIndex || (index + 1);
                        const dishName = dishContent?.title || dishContent?.name || `Dish ${dishNumber}`;

                        dishServingsBreakdown.push({
                            dishNumber: dishNumber,
                            dishName: dishName,
                            servings: dishTotalServings
                        });

                        totalServings += dishTotalServings;
                    });
                } else {
                    // Single dish - calculate total servings
                    leftoverGroup.forEach(leftover => {
                        const dishContent = contents[leftover.recipe_uuid || leftover.food_uuid];
                        totalServings += calculateTotalServingsForParticipants(participants, leftover, dishContent);
                    });
                }

                // Handle both consumer (date-based) and PRO (offset-based) data models for existing leftovers too
                let displayDate;
                if (firstLeftover.offset !== undefined) {
                    // PRO side: has leftoverOffset (integer) - use Day X format
                    displayDate = `Day ${firstLeftover.offset + 1}`;
                } else {
                    // Consumer side or fallback: use date
                    displayDate = firstLeftover.date || firstLeftover.scheduled_at;
                }

                return {
                    date: displayDate,
                    mealType: firstLeftover.meal,
                    participants: participantsDisplay,
                    servings: totalServings,
                    dishServingsBreakdown: dishServingsBreakdown,
                    isMultiDish: isMultiDish,
                    isPending: false
                };
            });
        }

        // Check if any leftovers are scheduled beyond 4 days for food safety warning
        const currentMeal = mealDishes && mealDishes[0];
        const leftoversBeyondSafetyLimit = leftovers.some(leftover => {
            if (!leftover.date) return false;

            // Handle "Day X" format - just check if day number > 5 (Day 1 = today, Day 5+ = beyond 4 days)
            if (leftover.date.startsWith('Day ')) {
                const dayNumber = parseInt(leftover.date.replace('Day ', ''));
                return dayNumber > 5;
            }

            // Handle regular date format
            if (currentMeal && currentMeal.date) {
                const currentMealDate = new Date(currentMeal.date);
                const leftoverDate = new Date(leftover.date);
                const daysDiff = Math.floor((leftoverDate - currentMealDate) / (1000 * 60 * 60 * 24));
                return daysDiff > 4;
            }

            return false;
        });

        // Show section if we have leftovers OR extra servings (for consumer "Store for later")
        const hasLeftoversOrExtraServings = leftovers.length > 0 || (!isPro && extraServings > 0);

        if (leftovers.length === 0 && (isPro || extraServings === 0)) {
            return (
                <div className="leftover-allocations">
                    <div className="leftover-header">
                        <h3>Leftover Allocations</h3>
                    </div>
                    <div className="no-leftovers">
                        <p>{unassigned > 0 ? `You have ${unassigned} unassigned servings` : "No leftovers planned for this meal"}</p>
                        <button className="add-leftovers-btn" onClick={() => onAddEditLeftovers()}>
                            {unassigned > 0 ? "Assign your leftovers" : "Add Leftovers"}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="leftover-allocations">
                <div className="leftover-header">
                    <h3>Leftover Allocations</h3>
                    <button className="edit-leftovers-link" onClick={() => onAddEditLeftovers()}>
                        Edit Leftovers
                    </button>
                </div>
                <div className="leftover-list">
                    {leftovers.map((leftover, index) => (
                        <div key={index} className="leftover-item">
                            <div className="leftover-for">
                                <div className="leftover-date">
                                    {!leftover.date ?
                                        leftover.mealType :
                                        leftover.date.startsWith('Day ') ?
                                            `${leftover.date}, ${leftover.mealType}` :
                                            `${moment(leftover.date).format('dddd, MMM D')}, ${leftover.mealType}`
                                    }
                                </div>
                                <div className="leftover-participants">{leftover.participants}</div>
                            </div>
                            <div className="leftover-servings">
                                {leftover.dishServingsBreakdown ? (
                                    <div className="dish-servings-breakdown">
                                        {leftover.dishServingsBreakdown.map((dish, dishIdx) => (
                                            <div key={dishIdx} className="dish-serving-line">
                                                Dish {dish.dishNumber}: {dish.servings} serving{dish.servings !== 1 ? 's' : ''}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    `${leftover.servings} serving${leftover.servings !== 1 ? 's' : ''}`
                                )}
                            </div>
                        </div>
                    ))}
                    {!isPro && (<div key="extra-servings" className="extra-servings-item">
                        <div className="extra-servings-date">
                            Store for later
                            <div className="extra-servings-text">Batch cooking reserves</div>
                        </div>
                        <div className="create-extra-servings">
                            <div className="guest-count-controls">
                                <button
                                    className="guest-btn"
                                    onClick={() => {
                                        onChangeExtraServings(Math.max(0, extraServings - 1));
                                    }}
                                    disabled={extraServings <= 0}
                                >
                                    -
                                </button>
                                <span className="guest-count">{extraServings}</span>
                                <button
                                    className="guest-btn"
                                    onClick={() => {
                                        onChangeExtraServings(extraServings + 1);
                                    }}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className="extra-servings">
                            {extraServings} serving{extraServings !== 1 ? 's' : ''}{dishes.length > 1 ? ' per dish' : ''}
                        </div>
                    </div>)}
                    {unassigned ? <div key="unassigned" className="unassigned-item">
                        <div className="unassigned-date">
                            Unassigned
                            <div className="unassigned-text">Available for scheduling</div>
                        </div>
                        <div className="unassigned-servings">
                            {unassignedBreakdown ? (
                                <div className="dish-servings-breakdown">
                                    {unassignedBreakdown.map((dish, dishIdx) => (
                                        <div key={dishIdx} className="dish-serving-line">
                                            Dish {dish.dishNumber}: {dish.servings} serving{dish.servings !== 1 ? 's' : ''}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                `${unassigned} serving${unassigned !== 1 ? 's' : ''}`
                            )}
                        </div>
                    </div> : null}
                </div>

                {leftoversBeyondSafetyLimit && (
                    <Alert type="info" description={<>Following food safety guidelines, it is best to consume cooked food and leftovers between 3-4 days. It is recommended that anything consumed after these guidelines be frozen until to ready to eat.</>} />
                )}
            </div>
        );
    }
}
